import { resolveSimulationIdFromScorecard } from '@/domains/scoring';
import { ApiScoreCard, ApiScoreCardv2, ISimulation, ISimulationScore } from '@/types';

// ================================
// Types
// ================================

/** Union type for flexible scorecard handling. */
type THistoryScorecard = ApiScoreCard | ApiScoreCardv2;

/** Input contract for `mapSimulationWithHistory`. */
interface IMapSimulationWithHistoryParams {
  /** Simulation object that will be copied and augmented with `history`. */
  simulation: ISimulation;
  
  /**
   * Source score records used to derive attempt history.
   *
   * Note: This union is intentional because some call sites may provide
   * API scorecards while others may provide frontend score models.
   * API scorecards can contribute to history in this mapper because they
   * expose either `scenario_id` (v1) or `simulation_id` (v2) for matching.
   */
  scorecards: (ApiScoreCard | ApiScoreCardv2 | ISimulationScore)[];
}

/**
 * Type guard to check if a scorecard is an API scorecard that matches the given simulation.
 *
 * This function narrows the input scorecard to `THistoryScorecard` if it has the necessary
 * properties to be considered an API scorecard and if it matches the simulation by ID.
 */
const isMatchingHistoryScorecard = (
  scorecard: ApiScoreCard | ApiScoreCardv2 | ISimulationScore,
  simulation: ISimulation,
): scorecard is THistoryScorecard => {
  if (!('scenario_id' in scorecard) && !('simulation_id' in scorecard)) {
    return false;
  }

  const simulationId = resolveSimulationIdFromScorecard(scorecard);
  const currentSimulationId = simulation.id != null ? simulation.id.toString() : null;

  return simulationId === currentSimulationId;
};

const getHistorySessionId = (scorecard: THistoryScorecard): string => {
  return (scorecard.session_id ?? '').toString();
};

// ================================
// mapSimulationWithHistory
// ================================

/**
 * Maps a simulation with its history of attempts/scores.
 *
 * ## Behavior summary
 * 1. Keep all original simulation fields unchanged.
 * 2. Derive `history` by scanning provided `scorecards`.
 * 3. Include only records that:
 *    - are API scorecards (`ApiScoreCard` or `ApiScoreCardv2`), and
 *    - match the simulation through `scenario_id` or `simulation_id`.
 * 4. Convert each matched `session_id` to string for a consistent
 *    `history: string[]` output shape.
 *
 * @param simulation - The simulation to map
 * @param scorecards - Array of API scorecards or frontend scores to extract history from
 *
 * @returns The simulation with an added 'history' property containing session IDs of attempts
 */
export function mapSimulationWithHistory({
  simulation,
  scorecards,
}: IMapSimulationWithHistoryParams): ISimulation {
  // Build attempt history in input order to preserve chronological ordering
  // as provided by the caller. No sorting is intentionally applied here.
  const history = scorecards
    // Narrow union type to API scorecard items and keep only those that belong
    // to the current simulation.
    .filter((scorecard): scorecard is THistoryScorecard => {
      return isMatchingHistoryScorecard(scorecard, simulation);
    })
    // Normalize session IDs to strings; fallback to empty string when nullish.
    .map(getHistorySessionId);

  // Return a shallow copy of the original simulation with calculated history.
  // Existing `history` on the input simulation (if any) is intentionally
  // replaced by the newly computed value.
  return {
    ...simulation,
    history,
  };
}
